(() => {
  if (customElements.get('dblc-shop-look')) return;

  class DblcShopLook extends HTMLElement {
    connectedCallback() {
      this.onTriggerClick = this.onTriggerClick.bind(this);
      this.onTriggerPointerDown = this.onTriggerPointerDown.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onBlockDeselect = this.onBlockDeselect.bind(this);

      this.addEventListener('click', this.onTriggerClick);
      this.addEventListener('pointerdown', this.onTriggerPointerDown);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      window.addEventListener('resize', this.onResize);

      this.pinShopifyBlocks();
      // Reveal one card on load so the section’s interaction is obvious
      requestAnimationFrame(() => this.openDefault());
    }

    openDefault() {
      // Never close/replace an already-open card (click-away, editor deselect, etc.)
      if (this.querySelector('[data-dblc-hotspot].is-open')) return;

      // Wait until the image frame has height — otherwise the card pins to y≈0
      const frame = this.frameEl();
      const frameReady = frame && frame.getBoundingClientRect().height > 40;
      if (!frameReady) {
        const img = this.querySelector('.dblc-shop-look__frame img');
        if (img && !img.complete) {
          img.addEventListener('load', () => this.openDefault(), { once: true });
          return;
        }
        this._defaultOpenRetries = (this._defaultOpenRetries || 0) + 1;
        if (this._defaultOpenRetries < 12) {
          requestAnimationFrame(() => this.openDefault());
        }
        return;
      }

      // Only hotspots with “Open by default” enabled in the block settings
      const target = this.hotspots().find(
        (hotspot) =>
          hotspot.hasAttribute('data-open-default') &&
          hotspot.querySelector('[data-dblc-hotspot-card]')
      );
      if (!target) return;

      // Opening expands a large absolute card — hold scroll so the page doesn’t jump
      this.holdScroll(400);
      this.pinShopifyBlocks();
      this.open(target);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onTriggerClick);
      this.removeEventListener('pointerdown', this.onTriggerPointerDown);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      window.removeEventListener('resize', this.onResize);
      this.releaseScrollHold();
    }

    hotspots() {
      return Array.from(this.querySelectorAll('[data-dblc-hotspot]'));
    }

    /** Customizer scrolls 0×0 hotspot blocks into view and jumps the page — hold position briefly. */
    holdScroll(ms = 320) {
      const x = window.scrollX;
      const y = window.scrollY;
      this.releaseScrollHold();

      const hold = () => {
        if (window.scrollX !== x || window.scrollY !== y) {
          window.scrollTo(x, y);
        }
      };

      this._scrollHold = hold;
      window.addEventListener('scroll', hold);
      this._scrollHoldTimers = [0, 16, 50, 100, 200].map((t) => setTimeout(hold, t));
      this._scrollHoldEnd = setTimeout(() => this.releaseScrollHold(), ms);
    }

    releaseScrollHold() {
      if (this._scrollHold) {
        window.removeEventListener('scroll', this._scrollHold);
        this._scrollHold = null;
      }
      if (this._scrollHoldTimers) {
        this._scrollHoldTimers.forEach(clearTimeout);
        this._scrollHoldTimers = null;
      }
      if (this._scrollHoldEnd) {
        clearTimeout(this._scrollHoldEnd);
        this._scrollHoldEnd = null;
      }
    }

    onTriggerPointerDown(event) {
      const trigger = event.target.closest('[data-dblc-hotspot-trigger]');
      if (!trigger || !this.contains(trigger) || trigger.disabled) return;
      // Stop focus scroll-into-view on the zero-size hotspot (customizer jump)
      event.preventDefault();
    }

    onBlockSelect(event) {
      // target is often the .shopify-block wrapper, not the hotspot root
      const root = event.target;
      if (!root || !this.contains(root)) return;
      const hotspot =
        root.matches?.('[data-dblc-hotspot]')
          ? root
          : root.querySelector?.('[data-dblc-hotspot]');
      if (!hotspot) return;

      this.holdScroll(500);
      // Preview this hotspot’s card while editing its settings
      if (hotspot.querySelector('[data-dblc-hotspot-card]')) {
        this.open(hotspot);
      } else {
        this.closeAll();
      }
    }

    onBlockDeselect(event) {
      // Keep whatever card is open — do not close on click-away
      const root = event.target;
      if (!root || !this.contains(root)) return;
    }

    onTriggerClick(event) {
      const trigger = event.target.closest('[data-dblc-hotspot-trigger]');
      if (!trigger || !this.contains(trigger) || trigger.disabled) return;

      event.preventDefault();
      event.stopPropagation();
      this.holdScroll();

      const hotspot = trigger.closest('[data-dblc-hotspot]');
      if (!hotspot) return;

      // Already open — leave it; only another hotspot click swaps cards
      if (hotspot.classList.contains('is-open')) return;

      this.open(hotspot);
    }

    onResize() {
      this.pinShopifyBlocks();
      const open = this.querySelector('[data-dblc-hotspot].is-open');
      if (open) this.fitCard(open);
    }

    /** Keep .shopify-block sized to the dot (not the full image) for customizer select/scroll */
    pinShopifyBlocks() {
      const mobile = window.matchMedia('(max-width: 767.98px)').matches;
      this.hotspots().forEach((hotspot) => {
        const wrap = hotspot.closest('.shopify-block');
        if (!wrap) return;

        const x = mobile
          ? hotspot.getAttribute('data-pos-x-m') || hotspot.getAttribute('data-pos-x') || '50'
          : hotspot.getAttribute('data-pos-x') || '50';
        const y = mobile
          ? hotspot.getAttribute('data-pos-y-m') || hotspot.getAttribute('data-pos-y') || '50'
          : hotspot.getAttribute('data-pos-y') || '50';
        const size = mobile ? 34 : 54;
        const inset = size / 2;

        wrap.style.setProperty('position', 'absolute', 'important');
        wrap.style.setProperty('left', `${x}%`, 'important');
        wrap.style.setProperty('top', `${y}%`, 'important');
        wrap.style.setProperty('width', `${size}px`, 'important');
        wrap.style.setProperty('height', `${size}px`, 'important');
        wrap.style.setProperty('margin', `${-inset}px 0 0 ${-inset}px`, 'important');
        wrap.style.setProperty('overflow', 'visible', 'important');
        wrap.style.setProperty('pointer-events', 'none');
        wrap.style.setProperty('display', 'block', 'important');
      });
    }

    clearCardShift(card) {
      if (!card) return;
      card.style.removeProperty('--dblc-card-shift-x');
      card.style.removeProperty('--dblc-card-shift-y');
    }

    setCardSide(hotspot, side) {
      hotspot.classList.toggle('is-card-above', side === 'above');
      hotspot.classList.toggle('is-card-below', side === 'below');
    }

    resetCardSide(hotspot) {
      hotspot.classList.remove('is-card-above', 'is-card-below');
    }

    frameEl() {
      return (
        this.querySelector('[data-dblc-shop-look-hotspots]') ||
        this.querySelector('.dblc-shop-look__hotspots') ||
        this.querySelector('.dblc-shop-look__frame')
      );
    }

    /** Absolute pin: image bottom-center, scrolls with the image */
    pinMobileCard(hotspot, card) {
      this.resetCardSide(hotspot);
      const frame = this.frameEl();
      if (!frame) return;

      const frameRect = frame.getBoundingClientRect();
      const hotspotRect = hotspot.getBoundingClientRect();
      const cardWidth = Math.min(249, window.innerWidth - 24);
      const targetLeft = frameRect.left + (frameRect.width - cardWidth) / 2;

      // bottom:0 → card bottom sits on the hotspot point; shift to image bottom + horizontal center
      const shiftX = targetLeft - hotspotRect.left;
      const shiftY = frameRect.bottom - hotspotRect.top;

      card.style.setProperty('--dblc-card-shift-x', `${shiftX}px`);
      card.style.setProperty('--dblc-card-shift-y', `${shiftY}px`);
    }

    fitCard(hotspot) {
      const card = hotspot.querySelector('[data-dblc-hotspot-card]');
      if (!card) return;

      const isMobile = window.matchMedia('(max-width: 767.98px)').matches;

      if (isMobile) {
        this.pinMobileCard(hotspot, card);
        return;
      }

      this.clearCardShift(card);

      requestAnimationFrame(() => {
        const pad = 12;
        let shiftX = 0;
        let shiftY = 0;

        const preferBelow = hotspot.classList.contains('is-bottom');
        this.setCardSide(hotspot, preferBelow ? 'below' : 'above');
        let rect = card.getBoundingClientRect();
        const hotspotRect = hotspot.getBoundingClientRect();

        // Only clamp to the viewport when the hotspot is on-screen.
        // Off-screen (e.g. default-open below the fold) must stay on the image —
        // otherwise shiftY pulls the card to the top of the page.
        const hotspotInView =
          hotspotRect.bottom > pad && hotspotRect.top < window.innerHeight - pad;

        if (hotspotInView) {
          if (rect.top < pad) {
            this.setCardSide(hotspot, 'below');
            rect = card.getBoundingClientRect();
          } else if (rect.bottom > window.innerHeight - pad) {
            this.setCardSide(hotspot, 'above');
            rect = card.getBoundingClientRect();
          }

          if (rect.left < pad) shiftX = pad - rect.left;
          else if (rect.right > window.innerWidth - pad) {
            shiftX = window.innerWidth - pad - rect.right;
          }

          if (rect.top < pad) shiftY = pad - rect.top;
          else if (rect.bottom > window.innerHeight - pad) {
            shiftY = window.innerHeight - pad - rect.bottom;
          }
        }

        card.style.setProperty('--dblc-card-shift-x', `${shiftX}px`);
        card.style.setProperty('--dblc-card-shift-y', `${shiftY}px`);
      });
    }

    clearClosing(hotspot) {
      if (!hotspot) return;
      if (hotspot._dblcCloseTimer) {
        clearTimeout(hotspot._dblcCloseTimer);
        hotspot._dblcCloseTimer = null;
      }
      hotspot.classList.remove('is-closing');
    }

    setOpenState(hotspot, isActive) {
      const trigger = hotspot.querySelector('[data-dblc-hotspot-trigger]');
      const card = hotspot.querySelector('[data-dblc-hotspot-card]');

      if (isActive) {
        this.clearClosing(hotspot);
        hotspot.classList.add('is-open');
        if (trigger) trigger.setAttribute('aria-expanded', 'true');
        if (card) {
          card.setAttribute('aria-hidden', 'false');
          card.removeAttribute('inert');
        }
        return;
      }

      // Closing (or already closed): hide immediately so only one card stays visible
      this.clearClosing(hotspot);
      hotspot.classList.remove('is-open', 'is-closing');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (card) {
        card.setAttribute('aria-hidden', 'true');
        card.setAttribute('inert', '');
        this.clearCardShift(card);
        this.resetCardSide(hotspot);
      }
    }

    open(hotspot) {
      const card = hotspot.querySelector('[data-dblc-hotspot-card]');
      // Position before open so the card doesn't flash at the dot
      if (card && window.matchMedia('(max-width: 767.98px)').matches) {
        this.pinMobileCard(hotspot, card);
      }

      // One open at a time: close every other hotspot, then open this one
      this.hotspots().forEach((item) => {
        this.setOpenState(item, item === hotspot);
      });
      this.fitCard(hotspot);
    }

    closeAll() {
      this.hotspots().forEach((item) => this.setOpenState(item, false));
    }
  }

  customElements.define('dblc-shop-look', DblcShopLook);
})();
